import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, type Ref, watch } from 'vue'
import { PluginKey, UserRightsEnum } from '@/generated'
import { useUserStore } from './user'
import { withMinimumDuration } from '@/util/withMinimumDuration'
import { AkiPredictionsService } from '@/services/telehealthApi/AkiPredictionsService'
import { useAsyncState } from '@vueuse/core'

export const usePluginsStore = defineStore('plugins', () => {
  const { user } = storeToRefs(useUserStore())
  const enabledPlugins: Ref<PluginKey[]> = ref([])

  function setEnabledPlugins(plugins: PluginKey[]) {
    enabledPlugins.value = plugins
  }

  const isEdsEnabled = computed(() => enabledPlugins.value.includes(PluginKey.EDS))
  const hasAkiPlugin = computed(() => enabledPlugins.value.includes(PluginKey.AKI))
  const MINIMUM_LOADING_DURATION_MS = 300

  const {
    state: isAkiBackendEnabled,
    isLoading: isAkiEnabledLoading,
    execute: fetchAkiFeatureStatus
  } = useAsyncState(
    async () => {
      return await withMinimumDuration(
        AkiPredictionsService.isAkiFeatureEnabled(),
        MINIMUM_LOADING_DURATION_MS
      )
    },
    false,
    { immediate: false }
  )

  const isAkiEnabled = computed(() => hasAkiPlugin.value && isAkiBackendEnabled.value)

  watch(
    hasAkiPlugin,
    (hasPlugin) => {
      if (hasPlugin) {
        fetchAkiFeatureStatus()
      } else {
        isAkiBackendEnabled.value = false
      }
    },
    { immediate: true }
  )

  const isConnectEnabled = computed(
    () =>
      enabledPlugins.value.includes(PluginKey.CONNECT) ||
      user.value?.currentRole === UserRightsEnum.TCC_IT
  )

  return {
    enabledPlugins,
    setEnabledPlugins,
    isEdsEnabled,
    isConnectEnabled,
    isAkiEnabledLoading,
    isAkiEnabled
  }
})
